import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const currentDate = new Date().toISOString().split('T')[0];

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            variant="contained"
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">Privacy Policy</Typography>
        </Box>

        <Paper sx={{ p: 4 }}>
          <Typography variant="body1" paragraph>
            Last updated: {currentDate}
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            Welcome to EQ Emblem ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            2. Age Restriction
          </Typography>
          <Typography variant="body1" paragraph>
            Our service is intended for users who are 18 years of age or older. We do not knowingly collect information from individuals under 18 years of age.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            3. Information We Collect
          </Typography>
          <Typography variant="h6" gutterBottom>
            3.1 Information You Provide
          </Typography>
          <Typography variant="body1" component="div">
            <ul>
              <li>Authentication information through Auth0 (email, name, profile picture)</li>
              <li>Character and guild information related to EverQuest</li>
              <li>Optional profile information:
                <ul>
                  <li>Facebook profile link</li>
                  <li>Age</li>
                  <li>Gender</li>
                </ul>
              </li>
              <li>Any messages sent through our internal messaging system</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom>
            3.2 Automatically Collected Information
          </Typography>
          <Typography variant="body1" component="div">
            <ul>
              <li>Usage data through Google Analytics</li>
              <li>Technical information about your device and internet connection</li>
              <li>Internal tracking data about how you use our service</li>
            </ul>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            4. How We Use Your Information
          </Typography>
          <Typography variant="body1" component="div">
            We use your information to:
            <ul>
              <li>Provide and maintain our service</li>
              <li>Improve and personalize user experience</li>
              <li>Analyze usage patterns and optimize our service</li>
              <li>Enable communication between users</li>
              <li>Generate anonymous, aggregated statistics about our community</li>
              <li>Communicate with you about updates or changes to our service</li>
            </ul>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            5. Data Sharing and Third Parties
          </Typography>
          <Typography variant="h6" gutterBottom>
            5.1 We share your information with:
          </Typography>
          <Typography variant="body1" component="div">
            <ul>
              <li>Auth0 for authentication services</li>
              <li>Google Analytics for usage analysis</li>
              <li>Other users (limited to information you choose to make public)</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom>
            5.2 Future Considerations:
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to display advertisements in the future, which may involve sharing non-personal data with advertising partners.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            6. Data Retention and Deletion
          </Typography>
          <Typography variant="body1" component="div">
            <ul>
              <li>Public-facing data is removed immediately upon account deletion</li>
              <li>Backend data is completely removed within 30 days of account deletion</li>
              <li>We retain the right to keep anonymized, aggregated data indefinitely</li>
              <li>You may request complete deletion of your data by contacting us</li>
            </ul>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            7. Your Rights
          </Typography>
          <Typography variant="body1" component="div">
            You have the right to:
            <ul>
              <li>Access your personal information</li>
              <li>Request deletion of your data</li>
              <li>Object to processing of your data</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            8. Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the internet is 100% secure.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            9. Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            10. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy, please contact us at [Your Contact Information].
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            11. Legal Basis for Processing (GDPR)
          </Typography>
          <Typography variant="body1" paragraph>
            For users in the EEA, we process your personal data based on:
            <ul>
              <li>Your consent</li>
              <li>The necessity to perform our contract with you</li>
              <li>Our legitimate interests in providing and improving our service</li>
              <li>Legal obligations we must comply with</li>
            </ul>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            12. International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            We store and process your data in the United States. By using our service, you consent to the transfer of your data to locations outside your country of residence, where data protection laws may differ.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy; 